'use client'

import { usePathname } from 'next/navigation'
import { ArrowRight } from 'lucide-react'
import Blogs from "./Blogs"
import Products from "./Products"
import Hero from "./Hero"
import AllProducts from "./AllProducts"
import { useHomeBlocks, type NormalizedHomeBlock } from "@/lib/commerce/hooks"
import { navigateUrl } from '@/utils/client-request'
import { formatImageURL } from '@/utils/util'

function CategoryStrip({ categories }: { categories: any[] }) {
  const pathname = usePathname()
  const parents = (categories || [])
    .filter((c: any) => c.level === 0)
    .sort((a: any, b: any) => (a.priority || 0) - (b.priority || 0))
    .slice(0, 4)

  if (parents.length === 0) return null

  return (
    <div className="cont pt-10 pb-4">
      <div className="flex items-end justify-between gap-4 mb-7">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] mb-2.5" style={{ color: '#a8a8b3' }}>Дэлгүүр хэс</div>
          <h2 className="text-2xl sm:text-[34px] font-semibold tracking-[-0.02em] leading-none" style={{ fontFamily: 'Gilroy, Inter, sans-serif', color: 'white' }}>
            Ангилал
          </h2>
        </div>
        <a href={navigateUrl('/product', pathname)} className="text-[13px] font-medium inline-flex items-center gap-1.5 px-4 h-9 rounded-full shrink-0" style={{ color: 'rgba(255,255,255,0.9)', background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}>
          Бүх ангилал <ArrowRight className="w-3.5 h-3.5" />
        </a>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {parents.map((c: any, i: number) => {
          const href = navigateUrl(`/product?category=${c.slug || c.id}`, pathname)
          const childCount = (c.children || []).length
          return (
            <a
              key={c.id}
              href={href}
              className="group block relative aspect-4/5 rounded-2xl overflow-hidden cursor-pointer transition-colors hover:bg-white/5"
              style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}
            >
              {c.thumbnail && (
                <>
                  <img src={formatImageURL(c.thumbnail)} alt="" loading="lazy" decoding="async" className="absolute inset-0 size-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03] motion-reduce:transition-none" />
                  <span aria-hidden className="absolute inset-0 bg-linear-to-t from-[#0a0a0c]/90 via-[#0a0a0c]/25 to-[#0a0a0c]/45" />
                </>
              )}
              <div className="absolute top-4 left-4 text-[11px] font-mono tabular-nums tracking-[0.2em]" style={{ color: c.thumbnail ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.4)' }}>
                {String(i + 1).padStart(2, '0')} / {String(parents.length).padStart(2, '0')}
              </div>
              <div className="absolute top-4 right-4 w-7 h-7 rounded-full inline-flex items-center justify-center transition-all group-hover:bg-white group-hover:text-[#0a0a0c]" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: 'white' }}>
                <ArrowRight className="h-3 w-3" />
              </div>
              <div className="absolute left-4 right-4 bottom-4">
                <div className="font-semibold tracking-[-0.03em] leading-[0.95] text-white line-clamp-2" style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 32 }}>
                  {c.name}
                </div>
                <div className="mt-3 pt-3 flex items-center justify-between text-[11px]" style={{ borderTop: '1px solid rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.6)' }}>
                  <span className="tabular-nums">{childCount > 0 ? `${childCount} дэд ангилал` : 'Ангилал'}</span>
                  <span className="uppercase tracking-[0.18em]">Үзэх</span>
                </div>
              </div>
            </a>
          )
        })}
      </div>
    </div>
  )
}

export default function Home({ vendor, homeBlocks: homeBlocksProp, subscription, activeCampaigns, categories }: { vendor: any, homeBlocks?: any[], subscription?: any, wishlist?: any, activeCampaigns?: any[], categories?: any[] }) {
  const blocks = useHomeBlocks({ vendor, homeBlocks: homeBlocksProp, subscription })

  const renderBlock = (b: NormalizedHomeBlock) => {
    switch (b.type) {
    case 'banner':
      return <Hero key={b.id} vendor={vendor} activeCampaigns={activeCampaigns || []} />
    case 'sale-products':
    case 'featured-products':
    case 'new-products':
    case 'category':
    case 'showcase':
      return <Products key={b.id} products={b.products || []} vendor={vendor} title={b.title || ''} limit={b.config?.limit} description={b.description} viewAllUrl={b.viewAllUrl} />
    case 'blogs':
      return <Blogs key={b.id} blogs={b.blogs || []} title={b.title} limit={b.config?.limit} description={b.description} />
    case 'all-products':
      return <AllProducts key={b.id} vendor={vendor} title={b.title} description={b.description} />
    default:
      return null
    }
  }

  const firstBannerIdx = blocks.findIndex(b => b.type === 'banner')
  const insertAfter = firstBannerIdx >= 0 ? firstBannerIdx : -1

  return (
    <div style={{ background: '#08080a' }} className="min-h-screen">
      {blocks.map((b, i) => (
        <div key={b.id}>
          {renderBlock(b)}
          {i === insertAfter && <CategoryStrip categories={categories || []} />}
        </div>
      ))}
      {insertAfter === -1 && <CategoryStrip categories={categories || []} />}
    </div>
  )
}
